'use client';

import { Box, Button, Card, CardContent, CircularProgress, Link, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HistoryIcon from '@mui/icons-material/History';
import TitleBar from '../layout/TitleBar';
import TomsVendingMachineInventorySection from '../toms-vending/TomsVendingMachineInventorySection';
import { useTomVendingMachinesList, useUpdateTomVendingMachine } from '@/hooks/useTomVendingMachine';
import { useNotification } from '@/contexts/NotificationContext';

const TomsVendingMachineDetailPage: React.FC = () => {
  const { locationId: locationIdParam, machineId: machineIdParam } = useParams();
  const locationIdFromUrl = parseInt(String(locationIdParam), 10);
  const machineId = parseInt(String(machineIdParam), 10);

  const invalidParams = Number.isNaN(locationIdFromUrl) || Number.isNaN(machineId);

  const { data: allMachines = [], isLoading, refetch } = useTomVendingMachinesList();
  const machine = useMemo(() => allMachines.find((m) => m.id === machineId), [allMachines, machineId]);
  const effectiveLocationId = machine?.location?.id ?? null;

  const [sortOrder, setSortOrder] = useState('0');
  const [assetId, setAssetId] = useState('');
  const [title, setTitle] = useState('');

  useEffect(() => {
    if (!machine) return;
    setSortOrder(String(machine.sortOrder));
    setAssetId(machine.assetId);
    setTitle(machine.title ?? '');
  }, [machine]);

  const updateMutation = useUpdateTomVendingMachine();
  const { showSuccess, showError } = useNotification();

  const saveDetails = () => {
    if (!machine) return;
    const so = parseInt(sortOrder, 10);
    if (Number.isNaN(so)) {
      showError('Order must be a number');
      return;
    }
    if (!assetId.trim()) {
      showError('Asset ID is required');
      return;
    }
    const payload: Record<string, unknown> = {
      id: machine.id,
      title: title.trim() || null,
      sortOrder: so,
      assetId: assetId.trim(),
    };
    if (machine.location?.id != null) {
      payload.locationId = machine.location.id;
    }
    updateMutation.mutate(payload, {
      onSuccess: () => {
        showSuccess('Machine updated');
        void refetch();
      },
      onError: (e: { message?: string }) => showError(e?.message || 'Failed to save'),
    });
  };

  const movementsQuery =
    effectiveLocationId != null
      ? `?vendingMachineId=${machineId}&locationId=${effectiveLocationId}`
      : `?vendingMachineId=${machineId}`;
  const movementsUrl = `/app/toms-vending/inventory/movements${movementsQuery}`;

  const breadcrumbs = useMemo(
    () => [
      { label: 'Home', href: '/app/dashboard' },
      { label: 'Locations', href: '/app/toms-vending/locations' },
      ...(effectiveLocationId != null
        ? ([
            {
              label: 'Location',
              href: `/app/toms-vending/locations/${effectiveLocationId}`,
            },
          ] as const)
        : []),
      { label: machine ? machine.title?.trim() || machine.assetId : 'Machine' },
    ],
    [effectiveLocationId, machine],
  );

  if (invalidParams) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Invalid URL</Typography>
      </Box>
    );
  }

  const pageTitle = machine ? machine.title?.trim() || machine.assetId : `Machine ${machineId}`;
  const backHref =
    effectiveLocationId != null
      ? `/app/toms-vending/locations/${effectiveLocationId}`
      : '/app/toms-vending/locations';
  const backLabel = effectiveLocationId != null ? 'Back to location' : 'Back to locations';

  return (
    <Box>
      <TitleBar
        breadcrumbs={breadcrumbs}
        title={pageTitle}
        rightContent={
          <Button
            component={RouterLink}
            to={movementsUrl}
            variant="outlined"
            startIcon={<HistoryIcon />}
            sx={{ textTransform: 'none' }}
          >
            Stock movements
          </Button>
        }
      />

      <Box sx={{ p: 2 }}>
        <Link
          component={RouterLink}
          to={backHref}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 2 }}
        >
          <ArrowBackIcon fontSize="small" />
          {backLabel}
        </Link>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : !machine ? (
          <Typography color="text.secondary">This vending machine was not found.</Typography>
        ) : (
          <>
            {machine.location == null && (
              <Typography color="warning.main" sx={{ mb: 2 }}>
                This machine is not assigned to a location. Assign it from a location’s “Manage vending
                machines” dialog before adjusting stock here.
              </Typography>
            )}
            {effectiveLocationId != null && effectiveLocationId !== locationIdFromUrl && (
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                This machine is assigned to a different location than the URL you used. Bookmarks may be
                stale after a move.
              </Typography>
            )}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                  Machine details
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Update order, asset ID, or display title. Use the section below for product lines on this
                  machine.
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'flex-start',
                  }}
                >
                  <TextField
                    label="Order"
                    type="number"
                    size="small"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    sx={{ width: 120 }}
                  />
                  <TextField
                    label="Asset ID"
                    size="small"
                    value={assetId}
                    onChange={(e) => setAssetId(e.target.value)}
                    required
                    sx={{ minWidth: 200, flex: '1 1 200px' }}
                  />
                  <TextField
                    label="Title"
                    size="small"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    sx={{ minWidth: 280, flex: '2 1 280px' }}
                  />
                </Box>
                <Button
                  variant="contained"
                  onClick={saveDetails}
                  disabled={updateMutation.isPending}
                  sx={{ mt: 2, textTransform: 'none' }}
                >
                  Save details
                </Button>
              </CardContent>
            </Card>

            {effectiveLocationId != null && (
              <>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Inventory on this machine
                </Typography>
                <TomsVendingMachineInventorySection
                  locationId={effectiveLocationId}
                  machineId={machineId}
                />
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default TomsVendingMachineDetailPage;
